import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { firstValueFrom } from 'rxjs';

import { FileManagerUploadedFilesComponent } from '@features/file-manager-uploaded-files/file-manager-uploaded-files.component';

import { PatientProtocolService } from '@models/patient-protocol/patient-protocol.service';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { TrimInputDirective } from '@shared/directives/trim-input.directive';
import {
  DocumentType,
  EntityType,
  EventType,
  PatientProtocolVersion,
} from '@shared/services/gql.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { Option } from '@shared/types/components.type';
import { Maybe } from '@shared/utils/utils';

export interface ProtocolVersionModalData {
  modalView: ProtocolModalView;
  name?: string;
  patientGroup?: Maybe<string>;
  protocolVersion?: Pick<PatientProtocolVersion, 'effective_date' | 'id' | 'name'>;
  protocolVersionOptions: Option[];
}

type ProtocolModalView = 'amendment' | 'create' | 'edit';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputComponent,
    IconComponent,
    FileManagerComponent,
    FileManagerUploadedFilesComponent,
    ReactiveFormsModule,
    NgSelectModule,
    NgClass,
    TrimInputDirective,
    TooltipDirective,
  ],
  templateUrl: './protocol-version-modal.component.html',
})
export class ProtocolVersionModalComponent implements OnInit {
  public ref = inject(CustomOverlayRef<unknown, ProtocolVersionModalData>);

  private authQuery = inject(AuthQuery);
  private readonly destroyRef = inject(DestroyRef);
  private eventService = inject(EventService);
  private mainQuery = inject(MainQuery);
  private patientProtocolService = inject(PatientProtocolService);

  copyFromVersionFc = new FormControl(null);
  effectiveDateFc = new FormControl('');
  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  metadata = {
    documentType: DocumentType.DOCUMENT_TRIAL_PROTOCOL,
    entity_type_id: EntityType.TRIAL_PROTOCOL,
  };
  protocolVersionFc = new FormControl('', [Validators.required]);
  @ViewChild('protocolVersion') protocolVersionInput!: InputComponent;

  ngOnInit(): void {
    this.setCopyFromFieldDefaultState();

    const isEditView = this.ref.data?.modalView === 'edit';

    if (isEditView) {
      this.protocolVersionFc.setValue(this.ref.data?.protocolVersion?.name || null);
      this.effectiveDateFc.setValue(this.ref.data?.protocolVersion?.effective_date || null);
    }

    this.protocolVersionFc.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.protocolVersionFc.invalid) {
        this.copyFromVersionFc.enable();
      } else {
        this.setCopyFromFieldDefaultState();
      }
    });
  }

  isInvalid = (): boolean => !!this.protocolVersionFc.invalid;

  onEdit = async () => {
    if (this.protocolVersionFc.value && this.ref.data) {
      const success = await this.patientProtocolService.updatePatientProtocolVersion({
        effective_date: this.effectiveDateFc.value,
        id: this.ref.data.protocolVersion.id,
        name: this.protocolVersionFc.value,
      });

      await firstValueFrom(
        this.eventService.processEvent$({
          entity_id: '',
          entity_type: EntityType.SITE,
          type: EventType.UPDATE_INVESTIGATOR_SCHEDULES,
        })
      );

      if (!success) {
        this.setDuplicateError();

        return;
      } else {
        const protocolVersionId = this.ref.data.protocolVersion.id;

        await this.uploadDocuments(protocolVersionId);
      }

      this.ref.close({
        effective_date: this.effectiveDateFc.value,
        success,
        updatedVersionName: this.protocolVersionFc.value,
      });
    }
  };

  onSave = async (): Promise<void> => {
    if (this.protocolVersionFc.value) {
      const isAmendment = this.ref.data?.modalView === 'amendment';

      const copyFromParams =
        isAmendment && this.copyFromVersionFc.value
          ? { copy_from_id: this.copyFromVersionFc.value }
          : {};

      const { data, errors } = await this.patientProtocolService.createPatientProtocolVersion({
        ...copyFromParams,
        effective_date: this.effectiveDateFc.value || null,
        name: this.protocolVersionFc.value,
      });

      if (errors.length) {
        this.setDuplicateError();

        return;
      } else {
        await this.uploadDocuments(data!.id);
        // await this.saveDiscontinuationRate(data!.id);
      }

      this.ref.close({
        version: { effective_date: data?.effective_date, label: data?.name, value: data?.id },
      });
    }
  };

  pathFn = () => {
    const trialId = this.mainQuery.getValue().trialKey;

    const versionId = this.ref.data?.protocolVersion?.id;

    return versionId ? `trials/${trialId}/document-library/${versionId}` : '';
  };

  private setCopyFromFieldDefaultState(): void {
    this.copyFromVersionFc.disable();
    this.copyFromVersionFc.reset();
  }

  private setDuplicateError() {
    this.protocolVersionFc.markAsTouched();
    this.protocolVersionInput.fc.setErrors({ duplicate_version: true });
  }

  private updateFileStoreBeforeUpload(files: File[], versionId: string) {
    const trialId = this.mainQuery.getValue().trialKey;

    files.forEach((file) => {
      this.fileManager?.fileStore.update(file.id, {
        ...file,
        bucket_key: `trials/${trialId}/document-library/${versionId}/${new Date().toISOString()}_${
          file.fileName
        }`,
      });
    });
  }

  private async uploadDocuments(versionId?: string) {
    if (!this.fileManager || !versionId) return;

    const files = this.fileManager.fileQuery.getAll().filter(({ uploaded }) => !uploaded);

    if (!files.length) return;

    await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: '',
        entity_type: EntityType.DOCUMENT,
        payload: JSON.stringify({
          file_names: files.map((f) => f.fileName).join(', '),
          uploaded_by: `${this.authQuery.getFullName()} ${this.authQuery.getEmail()}`,
        }),
        type: EventType.TRIAL_PROTOCOL_UPLOADED,
      })
    );

    if (this.ref.data?.modalView !== 'edit') {
      this.updateFileStoreBeforeUpload(files, versionId);
    }

    await this.fileManager.fileService.uploadFiles({
      filterLocalFilesByUploadedFlag: true,
      insertDocument: true,
      is_metadata_editable: false,
      metadata: {
        documentType: DocumentType.DOCUMENT_TRIAL_PROTOCOL,
        entity_type_id: EntityType.TRIAL_PROTOCOL,
      },
      overrideFilePath: undefined,
      removeFilesOnPartialFailure: true,
      showSuccess: true,
    });
  }
}
